import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { useSiteConfig, useAgents } from '../../context/CMSContext';

interface WhatsAppMenuProps {
  variant: 'float' | 'button';
  className?: string;
  trackAction?: () => void;
}

const extractNumber = (url: string): string => {
  const match = url.match(/wa\.me\/(\d+)/);
  return match ? match[1] : url;
};

export const WhatsAppMenu: React.FC<WhatsAppMenuProps> = ({ variant, className = '', trackAction }) => {
  const [open, setOpen] = useState(false);
  const siteConfig = useSiteConfig();
  const agents = useAgents();
  const containerRef = useRef<HTMLDivElement>(null);

  const whatsapp1 = siteConfig.social.whatsapp1;
  const whatsapp2 = siteConfig.social.whatsapp2;

  const buildLabel = (url: string | undefined, fallbackName: string) => {
    if (!url) return null;
    const number = extractNumber(url);
    const displayNumber = number.replace(/^852/, '');
    const agent = agents.find(
      (a) => a.whatsapp === number || a.whatsapp === displayNumber || `852${a.whatsapp}` === number
    );
    return agent ? `${agent.name} (${displayNumber})` : `${fallbackName} (${displayNumber})`;
  };

  const items = [
    { url: whatsapp1, label: buildLabel(whatsapp1, 'WhatsApp 1') },
    { url: whatsapp2, label: buildLabel(whatsapp2, 'WhatsApp 2') },
  ].filter((item): item is { url: string; label: string } => Boolean(item.url && item.label));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (items.length === 0) return null;

  const openUrl = (url: string) => {
    if (trackAction) trackAction();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 只有一個選項時直接跳轉，不顯示選單
  if (items.length === 1) {
    if (variant === 'float') {
      return (
        <a
          href={items[0].url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="透過 WhatsApp 聯絡我們"
          className={`fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#25d366] focus:ring-offset-2 ${className}`}
        >
          <MessageSquare className="h-7 w-7" fill="currentColor" />
        </a>
      );
    }

    return (
      <a
        href={items[0].url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackAction}
        className={`w-full sm:w-auto flex items-center justify-center gap-3 bg-green-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-green-700 transition-all transform hover:-translate-y-1 shadow-xl ${className}`}
      >
        <MessageSquare className="w-6 h-6" />
        WhatsApp 查詢
      </a>
    );
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(!open);
  };

  const handleItemClick = (url: string) => {
    openUrl(url);
    setOpen(false);
  };

  const menu = (
    <div className="rounded-xl bg-white shadow-xl border border-slate-100 overflow-hidden w-64">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
        <span className="text-sm font-bold text-slate-700">選擇 WhatsApp</span>
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      {items.map((item, idx) => (
        <button
          key={idx}
          onClick={() => handleItemClick(item.url)}
          className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-green-50 hover:text-green-700 transition-colors flex items-center gap-2"
        >
          <MessageSquare className="h-4 w-4" />
          {item.label}
        </button>
      ))}
    </div>
  );

  if (variant === 'float') {
    return (
      <div ref={containerRef} className={`fixed bottom-6 right-6 z-40 ${className}`}>
        {open && (
          <div className="absolute bottom-full right-0 mb-2">
            {menu}
          </div>
        )}
        <button
          onClick={handleToggle}
          aria-label="透過 WhatsApp 聯絡我們"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#25d366] focus:ring-offset-2"
        >
          <MessageSquare className="h-7 w-7" fill="currentColor" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20">
          {menu}
        </div>
      )}
      <button
        onClick={handleToggle}
        className="w-full sm:w-auto flex items-center justify-center gap-3 bg-green-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-green-700 transition-all transform hover:-translate-y-1 shadow-xl"
      >
        <MessageSquare className="w-6 h-6" />
        WhatsApp 查詢
      </button>
    </div>
  );
};

export default WhatsAppMenu;
